import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import DashboardFooter from "@/components/dashboard/DashboardFooter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import GmailIntegration from "@/components/dashboard/Integration/GmailIntegration";
import OutlookIntegration from "@/components/dashboard/Integration/OutlookIntegration";
import OtherIntegration from "@/components/dashboard/Integration/OtherClientsIntegration";
import IntegrationGuideHeader from "./IntegrationGuideHeader";
import { fetchCustomerByAccessId } from "@/services/api";
import { Customer } from "@/types/customer";

const IntegrationGuide: React.FC = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accessId = searchParams.get("accessId");
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [projectDeveloper, setProjectDeveloper] = useState<string | null>(null);
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);

  const dashboardUrl = `https://registry.silvaconsult.org/contribute/dashboard${
    accessId ? `?accessId=${accessId}` : ""
  }`;

  useEffect(() => {
    const fetchDetails = async () => {
      if (accessId) {
        try {
          const customer = await fetchCustomerByAccessId(accessId);
          setCustomer(customer);
          setProjectDeveloper(
            customer.account.toTransactions?.[0]?.project?.projectDeveloper
              ?.name || null
          );
        } catch (error) {
          console.error("Failed to fetch customer details:", error);
        }
      }
    };
    fetchDetails();
  }, [accessId]);

  // Handle video selection based on integration type
  const handleVideoSelection = (integration: string | null) => {
    const videoUrls: Record<string, string> = {
      gmail:
        "https://www.youtube.com/embed/begcKdlkkKU?si=KKIQ-hYo-aGNy75k&vq=hd1440",
      outlook:
        "https://www.youtube.com/embed/W9l7M0T-sCs?si=OC2CVQWtkmKeiEkm&vq=hd1440",
    };
    const videoUrl = integration ? videoUrls[integration] || null : null;
    setCurrentVideoUrl(videoUrl);
  };

  useEffect(() => {
    handleVideoSelection(activeAccordion);
  }, [activeAccordion]);

  // Safely render HTML content
  const renderHTML = (htmlString: string) => ({ __html: htmlString });

  // Render header section
  const HeaderSection = () => (
    <div className="relative p-6 mb-8">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url("/integrations/integration_guide_hero_background.svg")',
        }}
      />
      <div className="relative flex-1 p-4 max-w-[1400px] mx-auto py-8">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          <div className="flex-1 lg:pr-64">
            <h1 className="text-3xl font-bold mb-4">
              {t("integrationGuide.title")}
            </h1>
            <p className="text-gray-600">{t("integrationGuide.subtitle")}</p>
          </div>
          <img
            src={
              language === "de"
                ? "/integrations/signature_mockup_de.png"
                : "/integrations/signature_mockup_en.png"
            }
            alt="Signature Mockup"
            className="h-80 object-contain mt-6 lg:mt-0"
          />
        </div>
      </div>
    </div>
  );

  // Render integration accordion
  const IntegrationAccordion = () => (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      value={activeAccordion || undefined}
      onValueChange={setActiveAccordion}
    >
      {[
        {
          value: "gmail",
          title: t("integrationGuide.gmailSteps"),
          component: (
            <GmailIntegration
              dashboardUrl={dashboardUrl}
              projectDeveloper={projectDeveloper}
            />
          ),
        },
        {
          value: "outlook",
          title: t("integrationGuide.outlookSteps"),
          component: (
            <OutlookIntegration
              dashboardUrl={dashboardUrl}
              projectDeveloper={projectDeveloper}
            />
          ),
        },
        {
          value: "other",
          title: t("integrationGuide.otherClientsSteps"),
          component: (
            <OtherIntegration
              dashboardUrl={dashboardUrl}
              projectDeveloper={projectDeveloper}
            />
          ),
        },
      ].map((item) => (
        <AccordionItem
          key={item.value}
          value={item.value}
          className="mb-4"
          style={{ borderBottom: "none" }}
        >
          <AccordionTrigger className="flex flex-1 items-center justify-between py-3 px-4 font-medium text-lg transition-all bg-gray-100 rounded-lg">
            {item.title}
          </AccordionTrigger>
          <AccordionContent className="p-4 max-sm:overflow-x-hidden">
            <div className="max-w-full">{item.component}</div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  return (
    <div className="flex flex-col min-h-screen bg-white w-full max-sm:overflow-x-hidden">
      {/* Header */}
      <IntegrationGuideHeader customerName={customer?.friendlyName} />

      {/* Main Content */}
      <HeaderSection />
      <main className="max-sm:container flex-1 w-full lg:max-w-[1400px] mx-auto py-8 px-2 max-sm:px-12">
        <div className="bg-white rounded-lg w-full">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-20 w-full">
            <div className="w-full lg:w-2/3">
              <p
                className="mb-8 text-base"
                dangerouslySetInnerHTML={renderHTML(
                  t("integrationGuide.description")
                )}
              />
              <IntegrationAccordion />
            </div>
            <div className="w-full lg:w-1/3">
              <div className="w-full h-[240px] sticky top-[150px]">
                {currentVideoUrl && (
                  <iframe
                    title="Integration Video"
                    src={currentVideoUrl}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full rounded-lg"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <DashboardFooter />
    </div>
  );
};

export default IntegrationGuide;
